// src/components/LandingPage.jsx 
//add a review completed section
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import ScrollToTopButton from './ScrollToTopButton';
import StatisticsSection from './StatisticsCounter';

// Zeffy Modal Component
const ZeffyModal = ({ isOpen, onClose, formUrl, formType }) => {
  if (!isOpen) return null;
  
  return (
    <div className="zeffy-modal-overlay">
      <div className="zeffy-modal-container">
        <div className="zeffy-modal-header">
          <button className="zeffy-modal-close" onClick={onClose}>
            &times;
          </button>
          <span className="zeffy-modal-title">{formType} Form</span>
        </div>
        <iframe 
          src={formUrl}
          title="Zeffy Donation Form"
          className="zeffy-iframe"
          frameBorder="0"
        />
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({
    section1: true,
    section2: true,
    section3: false,
    section4: false,
    section5: false,
    section6: false,
    section7: false,
  });

  // Add new state for collapsed sections
  const [collapsed, setCollapsed] = useState({
    aboutUs: true,       
    understanding: true,
    zakahVsSadaqa: true,
    collection: true,
    distribution: true,
    guidance: true,
    faqs: true,
  });

  // Add state for FAQs
  const [faqs, setFaqs] = useState([]);

  // Add state for the modal
  const [modalOpen, setModalOpen] = useState(false);
  const [currentFormUrl, setCurrentFormUrl] = useState("");
  const [currentFormType, setCurrentFormType] = useState("");

  // Toggle function for collapsible sections
  const toggleSection = (section) => {
    setCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleGetStarted = () => {
    navigate('/main');
  };

  // Observer for animation trigger when the section is in view
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.animated-section');
      const triggerBottom = window.innerHeight * 0.9;

      sections.forEach((section, index) => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop < triggerBottom) {
          setIsVisible((prev) => ({
            ...prev,
            [`section${index + 1}`]: true,
          }));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Add useEffect to fetch FAQs
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const faqsCollection = collection(db, 'faqs');
        const faqsSnapshot = await getDocs(faqsCollection);
        const faqsList = faqsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFaqs(faqsList);
      } catch (error) {
        console.error("Error fetching FAQs: ", error);
      }
    };

    fetchFAQs();
  }, []);

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.4
      }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2
      }
    },
    tap: {
      scale: 0.95
    }
  };

  return (
    <motion.div 
      className="landing-page"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <motion.div 
        className="content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.8 }}
      >
        <motion.img 
          src="https://firebasestorage.googleapis.com/v0/b/tampazakahfund.appspot.com/o/tzf_logo_edited.png?alt=media&token=1f426ae0-408f-4c4d-bc80-0e713f8a89d0"
          alt="Tampa Zakah Fund Logo"
          style={{ maxWidth: '300px' }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        />
        
        <motion.div 
          className="action-buttons"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          {/* <motion.button
            className="action-button fitr-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => {
              console.log("Opening Zakat ul Fitr donation form");
              setCurrentFormUrl("https://www.zeffy.com/embed/donation-form/zakat-ul-fitr-2");
              setCurrentFormType("Zakat ul Fitr");
              setModalOpen(true);
            }}
          >
            Zakat ul Fitr
          </motion.button> */}

          <motion.button
            className="action-button zakah-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/donate/zakah')}
          >
            Pay your Zakah
          </motion.button>

          <motion.button
            className="action-button sadaqa-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/donate/sadaqa')}
          >
            Donate to Sadaqa Funds
          </motion.button>

          <motion.button 
            className="action-button apply-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => window.location.href = 'https://tampazakahapplications.netlify.app/signin'}
          >
            Apply for Assistance
          </motion.button>

          <motion.button 
            className="action-button calculator-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/zakah-calculator')}
          >
            Zakah Calculator
          </motion.button>
        </motion.div>

        {/* About Us Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section1 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('aboutUs')}
          >
            About Us {collapsed.aboutUs ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.aboutUs ? 0 : 'auto',
              opacity: collapsed.aboutUs ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.aboutUs && (
              <p className={`animated-section ${isVisible.section1 ? 'slide-right' : ''}`}>
                Tampa Zakah is a grassroots initiative under TMC (a 501(c)3 organization), dedicated to local collection and local
                distribution of Zakah and Sadaqah within the Tampa community. The mission of Tampa Zakah is rooted in the Islamic principle that the wealth belongs to Allah,
                and those with surplus of the wealth are obligated to support those in need of the community. 
              </p>
            )}
          </motion.div>
        </motion.div>

        {/* Understanding Zakah Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section3 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('understanding')}
          >
            Understanding Zakah {collapsed.understanding ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.understanding ? 0 : 'auto',
              opacity: collapsed.understanding ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.understanding && (
              <p className={`animated-section ${isVisible.section3 ? 'slide-up' : ''}`}>
                Zakah is one of the five pillars of Islam. It is an obligatory duty (Fardh) on every Muslim man and woman, who meets certain financial requirements (Nisab).
                The term, 'Zakah' means 'to purify' or 'to cleanse'. It reflects the belief that paying Zakah purifies one's wealth. Zakah has the potential to alleviate financial sufferings
                locally and globally.  Its purpose is to support the poor and the needy, ensuring that wealth is used responsibly in service to the community.
              </p>
            )}
          </motion.div>
        </motion.div>

        {/* Zakah vs Sadaqah Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section4 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('zakahVsSadaqa')}
          >
            Zakah vs. Sadaqah {collapsed.zakahVsSadaqa ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.zakahVsSadaqa ? 0 : 'auto',
              opacity: collapsed.zakahVsSadaqa ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.zakahVsSadaqa && (
              <p className={`animated-section ${isVisible.section4 ? 'slide-right' : ''}`}>
                Sadaqah is a voluntary act of charity. It can be given at any time, in any amount, and given to anyone who is in need. 
              </p>
            )}
          </motion.div>
        </motion.div>

        {/* Collection of Zakah Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section5 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('collection')}
          >
            Collection of Zakah {collapsed.collection ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.collection ? 0 : 'auto',
              opacity: collapsed.collection ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.collection && (
              <p className={`animated-section ${isVisible.section5 ? 'slide-up' : ''}`}>
                Zakah is due when a Muslim's Assets/wealth exceeds the Nisab (87.48 grams of gold or 612.36 grams of silver) and has remained above this threshold for a complete lunar year (حول).
                Depending upon the type of the assets/wealth, it is calculated at the rate of 2.5% of the total stored assets/wealth. Please refer the Zakah Calculator to calculate your Zakah due. 
              </p>
            )}
          </motion.div>
        </motion.div>

        {/* Zakah Distribution Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section6 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('distribution')}
          >
            Zakah Distribution {collapsed.distribution ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.distribution ? 0 : 'auto',
              opacity: collapsed.distribution ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.distribution && (
              <div className={`animated-section zakah-categories ${isVisible.section6 ? 'slide-up' : ''}`}>
                <p>Zakah can be distributed to eight categories of people (Qur'an, 9:60). They are:</p>
                <ol>
                  <li>Fuqarah <span className="category-description">(the destitute and poor, who need immediate relief and essentials)</span></li>
                  <li>Masakin <span className="category-description">(the needy who are not self-sufficient, even though they have some provision)</span></li>
                  <li>Mu'allafat-ul-Quloobuhum <span className="category-description">(To soften the hearts of the people who are inclined towards Islam)</span></li>
                  <li>Ameleen-a-alyha <span className="category-description">(the personnel who are employed for Zakah collection and distribution)</span></li>
                  <li>Fir-Riqab <span className="category-description">(freeing the slaves or prisoners; paying fines to free unjustly imprisoned people)</span></li>
                  <li>Gharemeen <span className="category-description">(those in debt and are having difficulty paying their debts)</span></li>
                  <li>Fi-Sabil-Allah <span className="category-description">(the people who are striving in the path of Allah)</span></li>
                  <li>Ibn-as-Sabil <span className="category-description">(the wayfarer; the person who becomes needy while traveling and can not meet his expenses)</span></li>
                </ol>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Guidance Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '20px'}} 
            className={`section-header ${isVisible.section7 ? 'fade-in' : ''}`}
            onClick={() => toggleSection('guidance')}
          >
            Guidance on Local Collection and Local Distribution of Zakah {collapsed.guidance ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.guidance ? 0 : 'auto',
              opacity: collapsed.guidance ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.guidance && (
              <div className={`animated-section schools-section ${isVisible.section7 ? 'slide-up' : ''}`}>
                <p className="hadith-text">
                  The Prophet Muhammad ﷺ, appointed Muadh (RA) as the Governor of Yemen and instructed him to collect Zakah from the local wealthy people and give it to the local poor. (Sahih al-Bukhari)
                </p>
                <p className="companion-text">
                  Similarly, Umar رضي الله عنه emphasized the importance of local distribution during his leadership.
                </p>
                
                <div className="schools-container">
                  <p className="schools-intro">All four major Islamic schools of thought agree on the preference for local collection and local distribution of Zakah:</p>
                  <ul className="schools-list">
                    <li><span className="school-name">Hanbali:</span> Zakah should be given to local recipients unless none are available.</li>
                    <li><span className="school-name">Shafi'i:</span> It is prohibited to transfer Zakah elsewhere if local recipients exist.</li>
                    <li><span className="school-name">Maliki:</span> Transferring Zakah outside the locality is impermissible unless absolutely necessary.</li>
                    <li><span className="school-name">Hanafi:</span> It is discouraged to transfer Zakah unless to needy relatives or a more deserving group.</li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* FAQs Section */}
        <motion.div 
          className="collapsible-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <h2 
            style={{fontSize: '21px'}} 
            className="section-header"
            onClick={() => toggleSection('faqs')}
          >
            Frequently Asked Questions {collapsed.faqs ? '▼' : '▲'}
          </h2>
          <motion.div
            initial={false}
            animate={{ 
              height: collapsed.faqs ? 0 : 'auto',
              opacity: collapsed.faqs ? 0 : 1
            }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {!collapsed.faqs && (
              <div className="animated-section">
                <div className="references">
                  <h3>REFERENCES</h3>
                  <ol>
                    <li>Mohammad Mazhar Hussaini, The Institution of Zakah, First Edition 2005.</li>
                    <li>AMJA, Assembly of Muslim Jurists of America.</li>
                    <li>IslamQA.org.</li>
                  </ol>
                </div>
                <div className="faqs-list">
                  {faqs.map((faq) => (
                    <div key={faq.id} className="faq-item">
                      <div className="faq-question">
                        <span>{faq.question}</span>
                      </div>
                      <div className="faq-answer">
                        {faq.answer}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Statistics Section */}
        <StatisticsSection />

        <motion.p 
          style={{fontSize: '20px', color: 'white', fontWeight: 'bold'}}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Disclaimer: Use a PC/Mac for best experience.
        </motion.p>
        
        <motion.p 
          style={{fontSize: '14px', color: 'gray'}}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          For more information, email here tampazakahfund@gmail.com ; developed and maintained by The Muslim Connection INC
        </motion.p>

        <button onClick={handleGetStarted} className="get-started-button">Get Started</button>
      </motion.div>
      <ScrollToTopButton />
      
      {/* Zeffy Donation Modal */}
      <ZeffyModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)}
        formUrl={currentFormUrl}
        formType={currentFormType}
      />
    </motion.div>
  );
};

export default LandingPage;
